import { Section } from '../pages/NotionEditor/types';

// Titles that indicate a manually-typed Table of Contents section.
// These are dropped on import — IndiePress generates its own TOC from section titles.
const TOC_TITLE_RE = /^(table\s+of\s+contents?|contents?\s+page|list\s+of\s+contents?)$/i;

const FRONT_MATTER_MAP: Record<string, string> = {
  dedication: 'dedication',
  foreword: 'foreword',
  preface: 'preface',
  acknowledgments: 'acknowledgments',
  acknowledgements: 'acknowledgments',
  prologue: 'prologue',
};

const END_MATTER_KEYWORDS = [
  'bibliography', 'references', 'index', 'appendix',
  'glossary', 'afterword', 'about the author', 'epilogue',
];

function classifyTitle(title: string): { type: Section['type']; baseId: string } {
  const lower = title.toLowerCase().trim();

  for (const [keyword, id] of Object.entries(FRONT_MATTER_MAP)) {
    if (lower === keyword || lower.startsWith(keyword)) {
      return { type: 'front-matter', baseId: id };
    }
  }
  for (const keyword of END_MATTER_KEYWORDS) {
    if (lower.includes(keyword)) {
      return {
        type: 'end-matter',
        baseId: `end-matter-${lower.replace(/\s+/g, '-').slice(0, 30)}`,
      };
    }
  }
  return { type: 'chapter', baseId: '' };
}

export function parseDocxSections(html: string): { sections: Section[]; tocRemoved: number } {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const children = Array.from(doc.body.children);

  // Prefer H1 as chapter boundary; fall back to H2 if no H1 found
  const hasH1 = children.some(el => el.tagName === 'H1');
  const hasH2 = children.some(el => el.tagName === 'H2');
  const splitTag = hasH1 ? 'H1' : hasH2 ? 'H2' : null;

  if (!splitTag) {
    return { sections: [{ id: 'chapter-1', title: 'Chapter 1', content: doc.body.innerHTML, type: 'chapter' }], tocRemoved: 0 };
  }

  const raw: Omit<Section, 'id'>[] = [];
  let currentTitle = '';
  let currentNodes: Element[] = [];
  let inSection = false;

  const flush = () => {
    if (!inSection) return;
    const { type } = classifyTitle(currentTitle);
    raw.push({ title: currentTitle, content: currentNodes.map(n => n.outerHTML).join(''), type });
  };

  for (const child of children) {
    if (child.tagName === splitTag) {
      const titleText = child.textContent?.trim();
      // Skip blank headings — Word documents often contain empty Heading-styled
      // paragraphs used for spacing or as TOC formatting artifacts
      if (!titleText) continue;
      flush();
      currentTitle = titleText;
      currentNodes = [];
      inSection = true;
    } else if (inSection) {
      currentNodes.push(child);
    }
    // Content before the first heading (title page artifacts) is intentionally discarded
  }
  flush();

  // All titled sections are passed through — empty-body sections are legitimate
  // structural labels (e.g. "Appendix A" with no intro paragraph).
  // Blank headings were already dropped above, which is the only filter needed.
  const tocSections = raw.filter(s => TOC_TITLE_RE.test(s.title.trim()));
  const finalRaw = raw.filter(s => !TOC_TITLE_RE.test(s.title.trim()));

  const frontMatterCounts: Record<string, number> = {};
  let chapterCount = 0;

  const sections = finalRaw.map(section => {
    const { type, baseId } = classifyTitle(section.title);

    if (type === 'front-matter') {
      frontMatterCounts[baseId] = (frontMatterCounts[baseId] || 0) + 1;
      const n = frontMatterCounts[baseId];
      return { ...section, id: n === 1 ? baseId : `${baseId}-${n}` };
    }
    if (type === 'end-matter') {
      return { ...section, id: `${baseId}-${Date.now()}` };
    }
    chapterCount++;
    return { ...section, id: `chapter-${chapterCount}` };
  });

  return { sections, tocRemoved: tocSections.length };
}
